import { type RefObject, useCallback, useLayoutEffect, useState } from 'react';

/** Vertical side selected for an overlay anchored to an element. */
export type AnchoredOverlaySide = 'top' | 'bottom';

/** Configuration for viewport-aware anchored overlay sizing. */
export interface UseAnchoredOverlayLayoutOptions<TElement extends HTMLElement> {
  /** Element used as the overlay anchor. */
  anchorRef: RefObject<TElement>;
  /** Whether the overlay is currently rendered. */
  isOpen: boolean;
  /** Space reserved between the anchor and overlay, in pixels. */
  gap?: number;
  /** Minimum inset kept between the overlay and viewport edge, in pixels. */
  viewportPadding?: number;
}

/** Viewport-aware vertical layout for an anchored overlay. */
export interface AnchoredOverlayLayout {
  /** Side of the anchor with the most available room. */
  side: AnchoredOverlaySide;
  /** Maximum usable overlay height, or null before it can be measured. */
  maxHeight: number | null;
}

const DEFAULT_GAP = 5;
const DEFAULT_VIEWPORT_PADDING = 16;
const initialLayout: AnchoredOverlayLayout = {
  side: 'bottom',
  maxHeight: null,
};

/**
 * Keeps an anchored overlay within the visual viewport as its anchor moves.
 *
 * @param options - Anchor, visibility, and desired viewport spacing.
 * @returns The roomiest vertical side and the height available on that side.
 */
export const useAnchoredOverlayLayout = <TElement extends HTMLElement>({
  anchorRef,
  isOpen,
  gap = DEFAULT_GAP,
  viewportPadding = DEFAULT_VIEWPORT_PADDING,
}: UseAnchoredOverlayLayoutOptions<TElement>): AnchoredOverlayLayout => {
  const [layout, setLayout] = useState<AnchoredOverlayLayout>(initialLayout);

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const visualViewport = window.visualViewport;
    const viewportTop = visualViewport?.offsetTop ?? 0;
    const viewportBottom =
      viewportTop + (visualViewport?.height ?? window.innerHeight);
    const spaceAbove = Math.max(
      0,
      rect.top - viewportTop - gap - viewportPadding
    );
    const spaceBelow = Math.max(
      0,
      viewportBottom - rect.bottom - gap - viewportPadding
    );
    const side: AnchoredOverlaySide =
      spaceBelow >= spaceAbove ? 'bottom' : 'top';
    const maxHeight = Math.floor(Math.max(spaceAbove, spaceBelow));

    setLayout((current) => {
      if (current.side === side && current.maxHeight === maxHeight) {
        return current;
      }

      return { side, maxHeight };
    });
  }, [anchorRef, gap, viewportPadding]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    measure();
    const visualViewport = window.visualViewport;

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    visualViewport?.addEventListener('resize', measure);
    visualViewport?.addEventListener('scroll', measure);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      visualViewport?.removeEventListener('resize', measure);
      visualViewport?.removeEventListener('scroll', measure);
    };
  }, [isOpen, measure]);

  return layout;
};
